// Footnotes.
//
// Ghost renders each Markdown card independently, so a post with footnotes
// spread across cards ends up with several `<section class="footnotes">`
// blocks AND colliding ids (every card restarts at fn1/fnref1). This:
//
//   Phase A0 — INLINE NOTES: `{a note written in place}` in any paragraph,
//     list item, or blockquote — rich text or markdown alike — is hoisted out
//     of the prose into a footnote. Authoring stays plain text, so email, RSS,
//     excerpts, and no-JS readers all see the note inline where it belongs;
//     this transform is web-only.
//
//   Phase A — GATHER: consolidates every note (markdown + inline) into one
//     `.footnotes` block at the foot of the article, numbered globally by the
//     order the refs appear in the text, so ids are unique and each
//     ref links to the right note. This alone lets a ref stay mid-article with
//     native image cards (or anything) between it and its note.
//
//   Phase B1 — POPOVER: on tap/click, a ref shows its note in a popover next
//     to it, so the reader never has to jump to the foot and back.
//
// The gathered foot block stays in the DOM as the no-JS / print fallback and
// as the popovers' source of truth. Runs only on the reading page.
//
// (Marginalia — sidenotes at the sidebar width on wide viewports — is the next
// phase and will layer on top of this same gathered/contract markup.)

export default function footnotes() {
    const content = document.querySelector(".gh-content");
    if (!content) return;

    const model = gather(content, extractInline(content));
    if (!model) return; // no footnotes on this post

    // Two reading modes over the same gathered notes, chosen by width:
    //   ≤1024px → popover (tap a ref, note appears beside it)
    //   ≥1025px → marginalia (notes as sidenotes in the right column,
    //             aligned to their refs), the layout where the sidebar
    //             already sits beside the text.
    const popover = createPopover(model);
    const reading = document.querySelector(".gh-reading");
    const sidebar = reading && reading.querySelector(".gh-sidebar-inner");
    const margin = sidebar ? createMarginalia(model, reading, sidebar) : null;

    if (!margin) {
        popover.enable(); // no sidebar to host marginalia — popover everywhere
        return;
    }

    const mq = window.matchMedia("(min-width: 1025px)");
    const apply = () => {
        if (mq.matches) {
            popover.disable();
            margin.enable();
        } else {
            margin.disable();
            popover.enable();
        }
    };
    apply();
    mq.addEventListener("change", apply);
}

// ---- Phase A0: inline {…} notes ---------------------------------------------

// A note may only open and close within the same block, and braces inside
// code, pre, kg-cards, or the footnote apparatus itself are literal text.
// Headings are deliberately excluded: toc.js has already read them, and a
// note in a heading would leak into the TOC.
const NOTE_BLOCKS = "p, li, blockquote";
const NOTE_SKIP = "pre, code, script, style, .kg-card, .footnotes";

function noteBlockOf(textNode) {
    const el = textNode.parentElement;
    if (!el || el.closest(NOTE_SKIP)) return null;
    return el.closest(NOTE_BLOCKS);
}

function extractInline(content) {
    // Find every block that contains a "{" anywhere in its prose, in document
    // order, then pull the notes out of each. Two passes because extraction
    // rewrites the text nodes the walker would be standing on.
    const blocks = [];
    const walker = document.createTreeWalker(content, NodeFilter.SHOW_TEXT);
    for (let n = walker.nextNode(); n; n = walker.nextNode()) {
        if (n.nodeValue.indexOf("{") === -1) continue;
        const block = noteBlockOf(n);
        if (block && blocks.indexOf(block) === -1) blocks.push(block);
    }

    const pairs = [];
    blocks.forEach((block) => {
        for (let pair = extractNote(block); pair; pair = extractNote(block)) {
            pairs.push(pair);
        }
    });
    return pairs;
}

function extractNote(block) {
    // One note per call, leftmost first; the caller loops. The text-node list
    // is rebuilt each time because the previous extraction split nodes.
    const nodes = [];
    const walker = document.createTreeWalker(block, NodeFilter.SHOW_TEXT);
    for (let n = walker.nextNode(); n; n = walker.nextNode()) {
        if (noteBlockOf(n) === block) nodes.push(n);
    }

    let i = 0;
    let start = -1;
    for (; i < nodes.length; i++) {
        start = nodes[i].nodeValue.indexOf("{");
        if (start !== -1) break;
    }
    if (i === nodes.length) return null; // no opener left

    let j = i;
    let end = -1;
    for (; j < nodes.length; j++) {
        end = nodes[j].nodeValue.indexOf("}", j === i ? start + 1 : 0);
        if (end !== -1) break;
    }
    if (j === nodes.length) return null; // unmatched "{" — leave it literal

    // Lift the whole `{…}` (note text may span inline elements — links,
    // italics — which the range carries along intact). Also take one plain
    // space before the opener, so the number hugs the preceding word the way
    // a typeset footnote mark does.
    const range = document.createRange();
    const before = start > 0 && nodes[i].nodeValue[start - 1] === " " ? 1 : 0;
    range.setStart(nodes[i], start - before);
    range.setEnd(nodes[j], end + 1);
    const frag = range.extractContents();

    // The ref replaces the note in the prose: same markup markdown-it emits,
    // so gather() and the CSS treat both kinds identically. gather() assigns
    // number, id, and href.
    const sup = document.createElement("sup");
    sup.className = "footnote-ref";
    const a = document.createElement("a");
    a.href = "#";
    sup.appendChild(a);
    range.insertNode(sup);

    // Shed the braces (and padding spaces) still sitting at the fragment's
    // outermost text nodes.
    const texts = [];
    const fw = document.createTreeWalker(frag, NodeFilter.SHOW_TEXT);
    for (let n = fw.nextNode(); n; n = fw.nextNode()) texts.push(n);
    if (texts.length) {
        const first = texts[0];
        const last = texts[texts.length - 1];
        first.nodeValue = first.nodeValue.replace(/^\s*\{\s*/, "");
        last.nodeValue = last.nodeValue.replace(/\s*\}\s*$/, "");
    }

    // Note item shaped like markdown-it's: <li><p>text <a backref>↩︎</a></p></li>
    const li = document.createElement("li");
    const p = document.createElement("p");
    p.appendChild(frag);
    const back = document.createElement("a");
    back.className = "footnote-backref";
    back.href = "#";
    back.textContent = "↩︎";
    p.appendChild(document.createTextNode(" "));
    p.appendChild(back);
    li.appendChild(p);

    return { ref: a, li };
}

// ---- Phase A: gather + renumber -------------------------------------------

function gather(content, inlinePairs) {
    const refs = Array.from(
        content.querySelectorAll("sup.footnote-ref > a[href^='#fn']")
    );
    const items = Array.from(content.querySelectorAll(".footnotes-list > li"));

    // Normal case: one markdown ref per note, in matching document order —
    // pair by order. (A malformed post with mismatched counts still gathers;
    // we just pair as far as the shorter list and warn.)
    const count = Math.min(refs.length, items.length);
    if (refs.length !== items.length) {
        // eslint-disable-next-line no-console
        console.warn(
            `[footnotes] ${refs.length} refs vs ${items.length} notes — pairing first ${count}`
        );
    }

    const all = [];
    for (let i = 0; i < count; i++) {
        all.push({ ref: refs[i], li: items[i] });
    }
    all.push(...inlinePairs);
    if (all.length === 0) return null; // no notes of either kind

    // Number by where each ref sits in the text, whichever syntax made it.
    all.sort((a, b) =>
        a.ref.compareDocumentPosition(b.ref) & Node.DOCUMENT_POSITION_FOLLOWING
            ? -1
            : 1
    );

    const section = document.createElement("section");
    section.className = "footnotes gh-footnotes";
    const list = document.createElement("ol");
    list.className = "footnotes-list";
    section.appendChild(list);

    const pairs = [];
    for (let i = 0; i < all.length; i++) {
        const num = i + 1;
        const { ref, li } = all[i];

        ref.href = `#fn-${num}`;
        ref.id = `fnref-${num}`;
        ref.textContent = String(num); // bare number; theme CSS renders it scarlet
        ref.dataset.fn = String(num);

        li.id = `fn-${num}`;
        li.classList.add("footnote-item");
        const back = li.querySelector("a.footnote-backref");
        if (back) back.href = `#fnref-${num}`;

        list.appendChild(li); // moves the <li> out of its old card list
        pairs.push({ num, ref, li });
    }

    // Drop the now-empty original sections and their separators.
    content.querySelectorAll("hr.footnotes-sep").forEach((hr) => hr.remove());
    content
        .querySelectorAll("section.footnotes, .footnotes")
        .forEach((s) => {
            if (!s.querySelector("li")) s.remove();
        });

    // Foot block: a "Notes" heading + the single list, appended inside
    // .gh-content so it inherits the theme's .gh-content .footnotes styling.
    const wrap = document.createElement("div");
    wrap.className = "gh-footnotes-wrap";
    const title = document.createElement("h2");
    title.className = "gh-footnotes-title";
    title.textContent = "Notes";
    wrap.appendChild(title);
    wrap.appendChild(section);
    content.appendChild(wrap);

    return { content, section, wrap, pairs };
}

// ---- Phase B1: popover -----------------------------------------------------

function createPopover({ pairs }) {
    // A single, fully self-controlled tooltip — no native Popover API. One
    // reused fixed-position element, shown/hidden with an `.is-open` class,
    // dismissed by our own document listeners (click-outside, Esc, re-tap the
    // ref). Deliberately boring: every open/close path is explicit and reasoned
    // here rather than relying on the browser's light-dismiss, whose
    // pointerdown/positioning interactions kept mis-firing.
    //
    // Returns { enable, disable } so the width controller can switch it off on
    // wide viewports (where marginalia takes over). While disabled, ref clicks
    // fall through to the note's default jump-link.
    if (typeof document === "undefined") return { enable() {}, disable() {} };

    let enabled = false;

    const pop = document.createElement("div");
    pop.className = "gh-fn-popover";
    pop.id = "gh-fn-popover";
    pop.setAttribute("role", "note");
    document.body.appendChild(pop);

    let openRef = null; // the ref whose note is currently shown, or null

    const place = () => {
        if (!openRef) return;
        const r = openRef.getBoundingClientRect();
        const pw = pop.offsetWidth;
        const ph = pop.offsetHeight;
        const pad = 8;
        // clientWidth/Height exclude the scrollbar; innerWidth includes it, so
        // clamping against innerWidth tucks the right edge under the scrollbar.
        const vw = document.documentElement.clientWidth;
        const vh = document.documentElement.clientHeight;
        let left = r.left + r.width / 2 - pw / 2;
        left = Math.max(pad, Math.min(left, vw - pw - pad));
        let top = r.bottom + pad; // below the ref by default…
        if (top + ph > vh - pad) top = r.top - ph - pad; // …flip up if it would clip
        top = Math.max(pad, top);
        pop.style.left = `${Math.round(left)}px`;
        pop.style.top = `${Math.round(top)}px`;
    };

    const close = () => {
        if (!openRef) return;
        pop.classList.remove("is-open");
        openRef.setAttribute("aria-expanded", "false");
        openRef = null;
    };

    const open = (ref, num, li) => {
        const clone = li.cloneNode(true);
        const back = clone.querySelector(".footnote-backref");
        if (back) back.remove();
        pop.innerHTML =
            `<span class="gh-fn-popover-num">${num}</span>` +
            `<div class="gh-fn-popover-body">${clone.innerHTML}</div>`;
        if (openRef) openRef.setAttribute("aria-expanded", "false");
        openRef = ref;
        ref.setAttribute("aria-expanded", "true");

        // Measure at full available width (left:0), then LOCK that width in px.
        // With an auto width the box shrink-to-fits against the gap to the
        // screen edge, so every reposition on scroll narrows it further; a
        // fixed px width is immune. CSS max-width:min(360px,90vw) caps the
        // measured value. Cleared width first so the measure is fresh.
        pop.style.width = "";
        pop.style.left = "0px";
        pop.style.top = "0px";
        pop.classList.add("is-open"); // must be visible before we can measure it
        pop.style.width = `${pop.offsetWidth}px`;

        place();
    };

    pairs.forEach(({ num, ref, li }) => {
        ref.setAttribute("aria-controls", pop.id);
        ref.setAttribute("aria-expanded", "false");
        ref.addEventListener("click", (e) => {
            if (!enabled) return; // wide viewport: leave the ref a jump-link
            e.preventDefault();
            e.stopPropagation(); // don't let the document handler treat this as an outside click
            if (openRef === ref) close(); // re-tap the open ref → close
            else open(ref, num, li);
        });
    });

    // Click anywhere that isn't a ref (handled above, propagation stopped) or
    // inside the popover itself → dismiss.
    document.addEventListener("click", (e) => {
        if (openRef && !pop.contains(e.target)) close();
    });
    document.addEventListener("keydown", (e) => {
        if (e.key === "Escape") close();
    });

    // Keep the open popover glued to its ref while the page moves.
    let raf = 0;
    const onMove = () => {
        if (!openRef || raf) return;
        raf = requestAnimationFrame(() => {
            raf = 0;
            place();
        });
    };
    window.addEventListener("scroll", onMove, { passive: true });
    window.addEventListener("resize", onMove);

    return {
        enable() {
            enabled = true;
        },
        disable() {
            enabled = false;
            close();
        },
    };
}

// ---- Phase B2: marginalia (wide viewports) ---------------------------------

function createMarginalia({ pairs, content }, reading, sidebarInner) {
    // Sidenotes in the right column, each aligned to its ref. The sidebar box
    // (TOC · metadata · Continue Reading) is left exactly as on any other post
    // — same look, just un-pinned on these posts so it scrolls away with the
    // page instead of covering the notes. The notes ride in a click-through
    // layer over the full-height column, each placed at its ref's Y and floored
    // below the box so the first notes don't slide under it.
    //
    // The layout tweaks live in footnotes.css under `.gh-has-marginalia`; here
    // we build the layer, position the notes, and realign on reflow. Returns
    // { enable, disable } for the width controller.
    const sidebar = sidebarInner.parentElement; // .gh-sidebar (position:relative anchor)
    const rail = document.createElement("div");
    rail.className = "gh-sidenotes";
    rail.setAttribute("aria-hidden", "true"); // notes already read at the foot; this is a visual echo
    sidebar.appendChild(rail);

    const notes = pairs.map(({ num, ref, li }) => {
        const note = document.createElement("aside");
        note.className = "gh-sidenote";
        note.id = `sidenote-${num}`;
        const clone = li.cloneNode(true);
        const back = clone.querySelector(".footnote-backref");
        if (back) back.remove();
        note.innerHTML =
            `<span class="gh-sidenote-num">${num}</span>` +
            `<div class="gh-sidenote-body">${clone.innerHTML}</div>`;
        rail.appendChild(note);
        return { num, ref, note };
    });

    let active = false;
    let raf = 0;

    // One downward pass: each note wants to sit at its ref's Y (relative to the
    // rail); if that would overlap the previous note, it's nudged down.
    const layout = () => {
        if (!active) return;
        const railTop = rail.getBoundingClientRect().top;
        const gap = 16;
        // Start below the sidebar box so the first notes don't tuck under it.
        let floor = Math.max(
            0,
            sidebarInner.getBoundingClientRect().bottom - railTop + gap
        );
        notes.forEach(({ ref, note }) => {
            const wanted = ref.getBoundingClientRect().top - railTop;
            const top = Math.max(wanted, floor);
            note.style.top = `${Math.round(top)}px`;
            floor = top + note.offsetHeight + gap;
        });
    };

    const schedule = () => {
        if (!active || raf) return;
        raf = requestAnimationFrame(() => {
            raf = 0;
            layout();
        });
    };

    // Hovering a ref lights its sidenote and vice-versa, so the reader can tell
    // which margin note belongs to which mark.
    notes.forEach(({ ref, note }) => {
        const on = () => {
            note.classList.add("is-active");
            ref.classList.add("is-fn-active");
        };
        const off = () => {
            note.classList.remove("is-active");
            ref.classList.remove("is-fn-active");
        };
        ref.addEventListener("mouseenter", on);
        ref.addEventListener("mouseleave", off);
        note.addEventListener("mouseenter", on);
        note.addEventListener("mouseleave", off);
        // Clicking the ref in margin mode flashes its (already visible) note
        // rather than jumping the page.
        ref.addEventListener("click", (e) => {
            if (!active) return;
            e.preventDefault();
            note.classList.add("is-active");
            window.setTimeout(() => note.classList.remove("is-active"), 900);
        });
    });

    // Realign after reflows: window resize, article size changes, late images.
    window.addEventListener("resize", schedule);
    if ("ResizeObserver" in window) {
        const ro = new ResizeObserver(schedule);
        ro.observe(content);
        ro.observe(rail);
    }
    content.querySelectorAll("img").forEach((img) => {
        if (!img.complete) img.addEventListener("load", schedule, { once: true });
    });

    return {
        enable() {
            active = true;
            reading.classList.add("gh-has-marginalia");
            // The class swaps the sidebar layout; measure once it has applied.
            requestAnimationFrame(layout);
        },
        disable() {
            active = false;
            reading.classList.remove("gh-has-marginalia");
        },
    };
}
